import React, { useEffect, useState,useMemo } from "react";

export default function About() {
  const [isScrolled, setIsScrolled] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // lock body scroll when menu is open
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const goTo = (href: string) => {
    setMobileOpen(false);

    // internal anchor on the same page
    if (href.startsWith("#")) {
      requestAnimationFrame(() => {
        const el = document.querySelector(href);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }

    // normal navigation
    window.location.href = href;
  };


  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);

    handleScroll(); // initialize on load
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
  const scrollToHash = () => {
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // first load
  const t = setTimeout(scrollToHash, 50);

  // if hash changes while on the page
  window.addEventListener("hashchange", scrollToHash);

  return () => {
    clearTimeout(t);
    window.removeEventListener("hashchange", scrollToHash);
  };
}, []);

    const pillars = useMemo(
    () => [
      {
        title: "Science-driven",
        body: "Grounded in cellular aging research and modern longevity science.",
      },
      {
        title: "Safety-first",
        body: "Food-grade, quality-controlled ingredients designed for consistent daily use.",
      },
      {
        title: "Daily ritual",
        body: "Simple to give, realistic to sustain — because longevity is built through consistency.",
      },
    ],
    []
  );


return (
<div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
{/* Navigation */}
<nav className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/98 backdrop-blur-md shadow-sm' : 'bg-white'} border-b border-gray-100`}>
<div className="max-w-6xl mx-auto px-6 py-4">
<div className="flex items-center justify-between">
<a href="/" className="flex items-center">
  <img
    src="/logo.svg"
    alt="Together Ever Logo"
    className="h-5 md:h-6 w-auto"
  />
</a>
<div className="hidden md:flex items-center space-x-8">
<a href="/" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">Home</a>
<a href="/science" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">Science</a>
<a href="/about" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">About</a>
<button className="bg-[#121212] text-white px-6 py-1 text-sm font-medium rounded-button hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wide">
Join Waitlist
</button>
</div>

{/* Mobile controls */}
      <div className="flex items-center gap-3 md:hidden">
        {/* Hamburger */}
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
          className="h-9 w-9 grid place-items-center rounded-lg hover:bg-gray-50 active:bg-gray-100"
        >
          <span className="block w-5">
            <span className={`block h-[2px] bg-gray-800 transition-all ${mobileOpen ? "translate-y-[6px] rotate-45" : ""}`} />
            <span className={`block h-[2px] bg-gray-800 mt-[5px] transition-all ${mobileOpen ? "opacity-0" : ""}`} />
            <span className={`block h-[2px] bg-gray-800 mt-[5px] transition-all ${mobileOpen ? "-translate-y-[8px] -rotate-45" : ""}`} />
          </span>
        </button>
      </div>
    </div>
  </div>

  {/* Mobile menu panel */}
  <div className={`md:hidden ${mobileOpen ? "block" : "hidden"}`}>
    {/* backdrop */}
    <div
      className="fixed inset-0 bg-black/20"
      onClick={() => setMobileOpen(false)}
    />

    {/* sheet */}
    <div
    className="
      fixed top-[64px] left-0 right-0
      rounded
      bg-white/55 backdrop-blur-xl backdrop-brightness-110 backdrop-saturate-150
      border border-white/40
      shadow-[0_18px_60px_rgba(0,0,0,0.16)]
      overflow-hidden
    "
  >
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/35 via-white/10 to-transparent" />
        <div className="relative px-4 py-4 space-y-2">
          <button
            onClick={() => goTo("/")}
            className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
          >
            Home
          </button>
          <button
            onClick={() => goTo("/science")}
            className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
          >
            Science
          </button>
          <button
            onClick={() => goTo("/about")}
            className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
          >
            About
          </button>
  </div>
  </div>
  </div>
  </nav>

      {/* Hero / Intro */}
      <section id="value" className="pt-10 pb-14 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <a href="/" className="text-sm text-gray-500 hover:text-black">
            ← Back
          </a>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <h1 className="text-4xl sm:text-5xl font-bold text-[#121212] tracking-tight leading-[1.02]">
                About Together Ever
              </h1>

              <p className="mt-6 text-xl text-gray-700 leading-relaxed max-w-2xl">
                A science-driven pet longevity venture translating aging biology into practical daily
                wellness for dogs and cats.
              </p>

              <p className="mt-4 text-base text-gray-600 leading-relaxed max-w-2xl">
                Shaped by a UC Berkeley perspective on cellular health, energy, and resilience, we
                focus on formulations that are simple, consistent, and realistic for everyday life.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="/waitlist"
                  className="inline-flex items-center justify-center rounded-full bg-[#121212] text-white px-6 py-2 text-sm font-semibold hover:bg-gray-800 transition"
                >
                  Join Waitlist
                </a>
                <a
                  href="mailto:support@togetherever.com"
                  className="inline-flex items-center justify-center rounded-full border border-gray-200 text-[#121212] px-6 py-2 text-sm font-semibold hover:border-gray-300 hover:bg-gray-50 transition"
                >
                  Contact
                </a>
              </div>
            </div>

            {/* Right accent block (subtle, high-end) */}
            <div className="lg:col-span-4">
              <div className="rounded-3xl border border-gray-100 bg-gradient-to-b from-[#FAF9F5] to-white p-6">
                <div className="text-xs uppercase tracking-[0.22em] text-gray-500">Principles</div>
                <div className="mt-3 text-sm text-gray-700 leading-relaxed">
                  We treat longevity as a daily practice: evidence-informed, safety-first, and built
                  for consistency.
                </div>
                <div className="mt-5 h-px bg-gray-200" />
                <div className="mt-5 text-xs text-gray-500 leading-relaxed">
                  Make the Good Days Last
                </div>
              </div>
            </div>
          </div>

          {/* Pillars */}
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {pillars.map((p) => (
              <div key={p.title} className="py-2">
                <div className="text-sm font-semibold text-[#121212]">{p.title}</div>
                <p className="mt-3 text-gray-600 leading-relaxed max-w-sm">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy + Story */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] text-gray-500">Our Philosophy</div>
              <h2 className="mt-4 text-3xl font-bold text-[#121212] tracking-tight">
                Longevity is built through small, consistent support.
              </h2>
              <p className="mt-5 text-gray-700 leading-relaxed max-w-md">
                If aging is increasingly understood as a biological process shaped by cellular
                health, energy metabolism, and systemic resilience, we believe this insight should
                first help the animals people love most.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-gray-100 bg-white p-8 sm:p-10">
                <div className="text-xs uppercase tracking-[0.22em] text-gray-500">Our Story</div>
                <p className="mt-4 text-gray-700 leading-relaxed text-lg">
                  Together Ever began with a way of thinking shaped at UC Berkeley that aging is
                  not random, but deeply connected to cellular health, energy, and resilience.
                </p>
                <p className="mt-5 text-gray-600 leading-relaxed">
                  Years of academic training and work in aging science formed a simple belief:
                  longevity is built through consistent support at the biological level.
                </p>
                <p className="mt-5 text-gray-600 leading-relaxed">
                  From this foundation came one question: if we understand more about how aging
                  works, why shouldn’t that insight first help the animals people consider family?
                </p>
                <p className="mt-5 text-gray-700 leading-relaxed">
                  Today, Together Ever carries this scientific mindset into practical daily wellness
                  for dogs and cats.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="pb-20 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-gray-500">Team</div>
              <h2 className="mt-3 text-3xl font-bold text-[#121212] tracking-tight">
                Who Makes This Happen
              </h2>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-10">
            {/* Woofie */}
            <div className="group">
              <div className="flex items-center gap-4">
                <img
                  src="woofie.jpg"
                  alt="Woofie"
                  className="w-16 h-16 rounded-2xl object-cover"
                />
                <div>
                  <div className="text-lg font-bold text-[#121212] leading-tight">Woofie</div>
                  <div className="text-sm text-[#baa573] font-semibold">Our Inspiration</div>
                </div>
              </div>

              <p className="mt-5 text-gray-600 leading-relaxed">
                Woofie’s warmth and vitality inspired a simple belief: pets deserve to age with
                comfort, dignity, and joy. She reminds us why this work matters every day.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="text-xs border border-gray-200 text-gray-600 px-3 py-1 rounded-full">
                  16 years old
                </span>
                <span className="text-xs border border-gray-200 text-gray-600 px-3 py-1 rounded-full">
                  Enjoys her life
                </span>
              </div>
            </div>

            {/* Conboy */}
            <div className="group">
              <div className="flex items-center gap-4">
                <img
                  src="Mike.jpg"
                  alt="Michael Conboy, PhD"
                  className="w-16 h-16 rounded-2xl object-cover object-top"
                />
                <div>
                  <div className="text-lg font-bold text-[#121212] leading-tight">
                    Michael Conboy, PhD
                  </div>
                  <div className="text-sm text-[#baa573] font-semibold">Scientific Co-Founder</div>
                </div>
              </div>

              <p className="mt-5 text-gray-600 leading-relaxed">
                Longevity scientist at UC Berkeley. His research explores how aging influences
                tissue health, regeneration, and cellular resilience, shaping the scientific
                foundation behind Together Ever.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="text-xs border border-gray-200 text-gray-600 px-3 py-1 rounded-full">
                  Aging biology
                </span>
                <span className="text-xs border border-gray-200 text-gray-600 px-3 py-1 rounded-full">
                  Regeneration
                </span>
              </div>
            </div>

            {/* Suoyi */}
            <div className="group">
              <div className="flex items-center gap-4">
                <img
                  src="Suoyi.jpg"
                  alt="Suoyi Feng"
                  className="w-16 h-16 rounded-2xl object-cover object-top"
                />
                <div>
                  <div className="text-lg font-bold text-[#121212] leading-tight">Suoyi Feng</div>
                  <div className="text-sm text-[#baa573] font-semibold">Business Co-Founder</div>
                </div>
              </div>

              <p className="mt-5 text-gray-600 leading-relaxed">
                Suoyi bridges aging science and venture execution, translating scientific thinking into real-world wellness. 
                His background spans molecular biology and business training at UC Berkeley.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="text-xs border border-gray-200 text-gray-600 px-3 py-1 rounded-full">
                  Venture execution
                </span>
                <span className="text-xs border border-gray-200 text-gray-600 px-3 py-1 rounded-full">
                  Product &amp; go-to-market
                </span>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 rounded-3xl border border-gray-100 bg-[#FAF9F5] p-8 sm:p-10">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-[0.22em] text-gray-500">
                  What we’re building
                </div>
                <h3 className="mt-3 text-2xl font-bold text-[#121212] tracking-tight">
                  A longevity program designed for real life.
                </h3>
                <p className="mt-4 text-gray-700 leading-relaxed max-w-2xl">
                  We’re working on formulation validation and manufacturing readiness to bring a
                  consistent, safety-first longevity ritual to pet families.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end">
                <a
                  href="/waitlist"
                  className="inline-flex items-center justify-center rounded-full bg-[#121212] text-white px-6 py-2 text-sm font-semibold hover:bg-gray-800 transition"
                >
                  Join Waitlist
                </a>
                <a
                  href="mailto:support@togetherever.com"
                  className="inline-flex items-center justify-center rounded-full border border-gray-200 text-[#121212] px-6 py-2 text-sm font-semibold hover:border-gray-300 hover:bg-white transition"
                >
                  Partnership
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#FAF9F5]">
        <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
        <div className="space-y-4">
              <a href="/" className="inline-block">
                <img
                  src="/logo.svg"
                  alt="Together Ever"
                  className="h-8 w-auto"
                />
              </a>

              <p className="text-[#121212] opacity-70 leading-relaxed max-w-xs">
                Support healthy aging in pets through longevity science
              </p>

              <p className="text-[#121212] opacity-70">
                support@togetherever.com
              </p>
          </div>

          <div></div>


          <div>
            <h4 className="font-bold text-[#121212] mb-4">Product</h4>
            <ul className="space-y-2 text-[#121212] opacity-70">
            <li><a href="/#hero" className="hover:text-[#C7A64C] transition-colors cursor-pointer">How It Works</a></li>
            <li><a href="/#revionyx" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Ingredients / Revionyx™</a></li>
            <li><a href="/#usage" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Usage Guide</a></li>
            <li><a href="/science#safety" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Safety & Quality</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-[#121212] mb-4">Venture</h4>
            <ul className="space-y-2 text-[#121212] opacity-70">
            <li><a href="/about#value" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Value</a></li>
            <li><a href="/about#team" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Team</a></li>
            <li><a href="/science" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Science</a></li>
            </ul>
          </div>

        </div>
        <div className="border-t border-gray-200 pt-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
        <p className="text-[#121212] opacity-70 text-sm mb-4 md:mb-0">
        © 2025 Together Ever™. All rights reserved.
        </p>
        <p className="text-[#121212] opacity-70 text-xs max-w-md text-center md:text-right">
        *These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.
        </p>
        </div>
        </div>
        </div>
        </footer>
    </div>
  );
}